/**
 * What the console's screens need in a region, by project role — least privilege instead of every
 * scope the role holds. Reads for everyone (queries, people, cohorts, catalog, Ask, replays); writes
 * (unmerge, cohorts) and customer SQL from member up; unmasked personal data (persons.pii) only for
 * admins and owners. Must stay a subset of the role's scopes (crates/auth scopes.rs role_scopes), or
 * the control plane answers 403.
 */
export function consoleRegionScopes(role: string): string[] {
  const read = ["query:read", "persons:read", "cohorts:read", "catalog:read", "ai:read", "replays:read", "alerts:read"];
  if (role === "viewer") return read;
  // alerts:write = findings feedback, mute and status.
  const member = [...read, "persons:write", "cohorts:write", "alerts:write", "sql.raw"];
  return role === "admin" || role === "owner" ? [...member, "persons.pii"] : member;
}

/**
 * Whether a project role holds a control-plane scope, mirroring crates/auth scopes.rs `role_scopes`:
 * viewers read everything; members read and write every data domain and hold `sql.raw` + `export`;
 * admins hold every scope except `billing.write`; owners hold all. Named scopes such as
 * `flags.production` and `experiments.ship` are therefore admin/owner only.
 */
export function roleHolds(role: string, scope: string): boolean {
  if (role === "owner") return true;
  if (role === "admin") return scope !== "billing.write";
  const named = !/:(read|write)$/.test(scope);
  if (role === "member") {
    if (named) return scope === "sql.raw" || scope === "export";
    return !scope.startsWith("projects:") && !scope.startsWith("org:") ? true : scope.endsWith(":read");
  }
  return !named && scope.endsWith(":read");
}
